import FaceliftText from "@/components/Atoms/FaceliftText";
import Show from "@/components/Molecules/Show";
import { colors } from "@/components/ui/theme";
import { Box, Grid, GridItem, Link } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router";
import { useGetNotification } from "@/hooks/service-hooks/notifications";
import { useNotificationContext } from "@/components/ui/notification-provider";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import { ChevronLeftIcon } from "@/assets/icons";
import NotificationDetailsSkeleton from "@/components/Templates/Notifications/NotificationDetailsSkeleton";
import { mapPriority, mapStatus } from "@/components/Templates/SupportTickets/constants";
import { getOrderRequestStatus } from "@/components/Templates/Returns/ReturnsOrderRequestModalContent";
import FaceliftButton from "@/components/Atoms/FaceliftButton";
import FaceliftDialog from "@/components/Atoms/FaceliftDialog";
import FaceliftDataLoader from "@/components/Atoms/FaceliftDataLoader";
import Preview from "@/components/Templates/BenefitVerification/Steps/Preview";
import DetailsView from "@/components/Templates/DetailsView";
import { parseOrderDetailsForModal } from "@/components/Templates/Requests/utils";
import { SupportTicketDetails } from "@/components/Templates/SupportTickets/SupportTicketDetails";
import { OrderReturnDetails } from "@/components/Templates/Returns";
import { useGetSingleBV } from "@/hooks/service-hooks/benefitsVerification";
import { useGetBvProducts } from "@/hooks/service-hooks/products";
import { useGetOrderById } from "@/hooks/service-hooks/requests";
import { useGetSupportTicket } from "@/hooks/service-hooks/supportTickets";
import { useToken } from "@chakra-ui/react";
import { useMemo } from "react";
import { extractDocumentName, formatDate, formatDateTime, transformBVRecordToPreview } from "@/utils";
import { getBvStatuses } from "@/components/Templates/BenefitVerification/BenefitVerificationTable/bv";
import { buildDocumentsUrl } from "@/utils/navigationItems";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const getNotificationDetails = (notificationData: Record<string, any>) => {
  let clinic = null
  let patient = null
  let physician = null
  let product = null
  let order = null
  let priorAuths = null
  let primaryInsurance = null
  try {
    clinic = JSON.parse(notificationData?.ta_Clinic || '{}')
    patient = JSON.parse(notificationData?.ta_Patient || '{}')
    physician = JSON.parse(notificationData?.ta_Physician2 || '{}')
    product = JSON.parse(notificationData?.ta_Product || '{}')
    order = JSON.parse(notificationData?.ta_Order || '{}')
    priorAuths = JSON.parse(notificationData?.priorAuths|| '{}')
    primaryInsurance = JSON.parse(notificationData?.primaryInsurance|| '{}')
  } catch (error) {
    console.error("Error parsing notification data", error)
    return []
  }
  switch (notificationData.entityName) {
    case 'ta_benefitsverification':
      let benefitVerification = null
      let productStatus = null
      if (notificationData?.benefitVerification) {
        benefitVerification = JSON.parse(notificationData?.benefitVerification || '{}')
        clinic = benefitVerification?.ta_Clinic
        patient = benefitVerification?.ta_Patient
        physician = benefitVerification?.ta_Physician2
        priorAuths = [{ ta_name: notificationData?.ta_name || '--' }]
        primaryInsurance = benefitVerification?.primaryInsurance
        productStatus = getBvStatuses()
      }

      return [
        { label: 'Clinic', value: clinic?.name || '--' },
        { label: 'Patient', value: patient?.fullname || '--' },
        { label: 'DOB', value: patient?.birthdate ? formatDate(patient?.birthdate) : '--' },
        { label: 'Physician', value: physician?.fullname || '--' },
        ...(benefitVerification?.ta_proceduredate ? [{
          label: 'Procedure Date',
          value: formatDate(benefitVerification?.ta_proceduredate)
        }] : []),
        ...(priorAuths && priorAuths?.length > 0 ? [{
          label: `Product${priorAuths?.length > 1 ? 's' : ''}`, value: priorAuths?.map((auth: any) => auth?.ta_name).join(', ') || '--'
        }] : []),
        ...(benefitVerification ? [{
          label: 'Status', value:
          notificationData?.ta_pa_status && productStatus ? productStatus[notificationData?.ta_pa_status as keyof typeof productStatus] : '--'
        }] : []),
        { label: 'Primary Insurance', value: primaryInsurance?.ta_name || '--' },
        { label: 'Primary Member ID', value: notificationData?.ta_primarymemberid || benefitVerification?.ta_primarymemberid || '--' },
        {
          label: 'Uploads',
          value: notificationData?.ta_portalbvupload || benefitVerification?.ta_portalbvupload ?
          extractDocumentName(notificationData?.ta_portalbvupload || benefitVerification?.ta_portalbvupload) : '--',
          url: notificationData?.ta_portalbvupload || benefitVerification?.ta_portalbvupload ?
            buildDocumentsUrl(
              notificationData?.ta_portalbvupload || benefitVerification?.ta_portalbvupload,
              String(clinic.accountid),
              clinic.name,
            ) : ''
        },
      ];

    case 'incident':
      return [
        { label: 'Ticket Number', value: notificationData?.ticketnumber || '--' },
        { label: 'Subject', value: notificationData?.title || '--' },
        { label: 'Clinic', value: clinic?.name || '--' },
        { label: 'Priority', value: notificationData?.prioritycode ? mapPriority(notificationData?.prioritycode)?.label : '--' },
        { label: 'Status', value: notificationData?.statuscode ? mapStatus(notificationData?.statuscode)?.label : '--' },
        { label: 'Description', value: notificationData?.description || '--' },
        {
          label: 'Uploads',
          value: notificationData?.ta_documentupload ? extractDocumentName(notificationData?.ta_documentupload) : '--',
          url: notificationData?.ta_documentupload ?
            buildDocumentsUrl(
              notificationData?.ta_documentupload,
              String(clinic.accountid),
              clinic.name,
            ) : ''
        },
      ];

    case 'ta_orderrequest':
      return [
        { label: 'Product', value: product?.ta_name ? `${product?.ta_name} (${product?.ta_qcode})` : '--' },
        { label: 'Clinic', value: clinic?.name || '--' },
        ...(notificationData?.ta_bvid_value ? [{ label: 'Patient', value: patient?.fullname || '--' }] : []),
        { label: 'Physician', value: physician?.fullname || '--' },
        { label: 'Quantity', value: notificationData?.ta_quantity || '--' },
        { label: 'Status', value: notificationData?.ta_status ? getOrderRequestStatus(notificationData?.ta_status) : '--' },
        {
          label: 'Order Status Date',
          value: notificationData?.ta_orderrequestdate ?
          formatDate(notificationData?.ta_orderrequestdate) : '--'
        },
        { label: 'SKU Description', value: notificationData?.ta_skudescription || '--' },
      ];

    case 'ta_returnrequest':
      return [
        { label: 'Customer Name', value: clinic?.name || '--' },
        { label: 'Tissue ID', value: notificationData?.ta_tissueid || '--' },
        { label: 'Street Address', value: notificationData?.ta_streetaddress || '--' },
        { label: 'City', value: notificationData?.ta_city || '--' },
        { label: 'State/Zip', value: notificationData?.ta_statezip || '--' },
        { label: 'Contact', value: notificationData?.ta_contact || '--' },
        { label: 'Phone', value: notificationData?.ta_phone || '--' },
      ];

    case 'ta_inventory':
      return [
        { label: 'Order', value: order?.ta_name || '--' },
        { label: 'Clinic', value: clinic?.name || '--' },
        { label: 'Product', value: product?.ta_name || '--' },
        { label: 'Size', value: notificationData?.ta_size || '--' },
        { label: 'FedEx Shipment Status', value: notificationData?.ta_fedexshipmentstatus || '--' },
        { label: 'Tracking Number', value: notificationData?.ta_trackingnumber || '--' },
        {
          label: 'Delivery Date', value: notificationData?.ta_deliverydate ?
            formatDate(notificationData?.ta_deliverydate) : '--'
        },
        {
          label: 'Expiration Date', value: notificationData?.ta_expirationdate ?
            formatDate(notificationData?.ta_expirationdate) : '--'
        },
        { label: 'Purchase Order Number', value: notificationData?.ta_purchaseordernumber || '--' }
      ];
    case 'invoice':
      return [
        { label: 'Invoice Number', value: notificationData?.invoiceNumber || '--' },
        { label: 'Clinic Name', value: notificationData?.clinicName || '--' },
        ...(notificationData?.reason ? [{
          label: 'Reason',
          value: notificationData?.reason
        }] : []),
        ...(notificationData?.amount ? [{
          label: 'Amount',
          value: notificationData?.amount
        }] : []),
        ...(notificationData?.invoiceDate ? [{
          label: 'Invoice Date',
          value: formatDate(notificationData?.invoiceDate)
        }] : []),
        ...(notificationData?.dueDate ? [{
          label: 'Due Date',
          value: formatDate(notificationData?.dueDate)
        }] : []),
        ...(notificationData?.patient ? [{
          label: 'Patient',
          value: notificationData?.patient
        }] : []),
        ...(notificationData?.tissueId ? [{
          label: 'Tissue ID',
          value: notificationData?.tissueId
        }] : []),
        ...(notificationData?.paymentAmount ? [{
          label: 'Payment Amount',
          value: notificationData?.paymentAmount
        }] : []),
        ...(notificationData?.paymentMethod ? [{
          label: 'Payment Method',
          value: notificationData?.paymentMethod
        }] : []),
        ...(notificationData?.paymentDate ? [{
          label: 'Payment Date',
          value: formatDate(notificationData?.paymentDate)
        }] : []),
        ...(notificationData?.payorByEmail ? [{
          label: 'Payor By Email',
          value: notificationData?.payorByEmail
        }] : []),
      ];
  }
  return null;
}

const NotificationDetailsView = () => {
  const { notificationId } = useParams();
  const navigate = useNavigate();
  const { userId } = useNotificationContext();
  const [brand600] = useToken('colors', ['brand.600']);

  const [showDetailsModal, setShowDetailsModal] = useState<'bv' | 'order' | 'incident' | 'return' | null>(null);
  const { data: notification, isFetching: isLoading, invalidate } = useGetNotification(userId || null, notificationId || null, !!userId && !!notificationId);

  // Get BV details
  const {
    mutate: getBVDetails,
    isLoading: isLoadingBVDetails,
    data: bvDetails,
  } = useGetSingleBV();

  const accountProducts = useMemo(() => {
    if (bvDetails?.priorAuths && bvDetails?._ta_clinic_value) {
      return (
        bvDetails?.priorAuths.map((product) => ({
            label: product.ta_name,
            value: product.ta_priorauthid as unknown as number,
            qCode: product.ta_priorauthid,
          })) || []
      );
    }
    return [];
  }, [bvDetails]);

  // Get order details
  const { data: orderDetails, isFetching: isOrderDetailsLoading } = useGetOrderById(
    showDetailsModal === 'order' && notification?.data?.primaryEntityId ? (notification?.data?.primaryEntityId as string) : null,
    showDetailsModal === 'order' && !!notification?.data?.primaryEntityId,
  );

  // Get ticket details
  const { data: ticketDetails, isFetching: isTicketDetailsLoading } = useGetSupportTicket(
    showDetailsModal === 'incident' && notification?.data?.primaryEntityId ? (notification?.data?.primaryEntityId as string) : null,
    showDetailsModal === 'incident' && !!notification?.data?.primaryEntityId,
  );

  // Note: OrderReturnDetails component handles its own data fetching internally

  // Handle button click
  const handleSeeDetails = () => {
    if (!notification?.data?.entityName || !notification?.data?.primaryEntityId) return;
    
    const entityName = notification?.data?.entityName;
    if (entityName === 'ta_benefitsverification') {
      let entityId = null;
      if (notification?.data?.benefitVerification) {
        const bv = JSON.parse(notification?.data?.benefitVerification as string) || {};
        entityId = bv?.ta_benefitsverificationid || '';
      } else {
        entityId = (notification?.data?.primaryEntityId as string);
      }
      setShowDetailsModal('bv');
      getBVDetails(entityId);
    } else if (entityName === 'ta_orderrequest') {
      setShowDetailsModal('order');
    } else if (entityName === 'incident') {
      setShowDetailsModal('incident');
    } else if (entityName === 'ta_returnrequest') {
      setShowDetailsModal('return');
    }
  };

  useEffect(() => {
    if (!isLoading && !!notification?.notificationId) {
      invalidate();
    }
  }, [isLoading, notification?.notificationId, invalidate]);

  return (
    <Box
      p={{ base: 4, md: 8 }}
      bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            onClick={() => navigate('/notifications')}
            bg="linear-gradient(90deg, {colors.brand.solid} 0%, {colors.brand.solidSecondary} 100%)"
            color="white"
            padding={2}
            borderRadius="full"
            cursor="pointer"
          >
            <ChevronLeftIcon width={10} height={10} />
          </Box>
          <FaceliftText fontWeight={600} fontSize="x-large">
            Notification Details
          </FaceliftText>
        </Box>
      </Box>
      <Box minH={700} bg={{ base: 'white', _dark: colors.tabelCellDark }} p={4} mt={4} borderRadius="md">
        <Show when={isLoading}>
          <NotificationDetailsSkeleton detailRows={8} />
        </Show>
        <Show when={!isLoading && !notification?.notificationId}>
          <Box textAlign="center" py={8}>
            <FaceliftText fontSize="md" color="gray.500" fontWeight="bold">
              No notification found
            </FaceliftText>
          </Box>
        </Show>
        <Show when={!isLoading && !!notification?.notificationId}>
          <Box overflowY="auto" display="flex" flexDirection="column" gap={6}>
            <Box bg={{ base: 'gray.100', _dark: 'gray.900' }} p={4} px={6} borderRadius="md">
              <Box borderLeft="4px solid" borderImage="linear-gradient(180deg, {colors.brand.solid} 0%, {colors.brand.solidSecondary} 100%) 1" pl={2}>
                <FaceliftText fontSize="lg" fontWeight={600}>{notification?.title || '-'}</FaceliftText>
                <FaceliftText fontSize="md" fontWeight={600}>{notification?.body || '-'}</FaceliftText>
                {notification?.by ? <FaceliftText fontSize="sm" fontWeight={500}>by {notification?.by}</FaceliftText> : null}
                <FaceliftText fontSize="xs">{notification?.createdOn && (
                  <Box fontSize="2xs" fontWeight="400" opacity={0.7}>
                    {formatDateTime(notification?.createdOn)}
                  </Box>
                )}</FaceliftText>
              </Box>
            </Box>
            <Box>
              <Box display="flex" flexDirection="column" gap={2}>
                {getNotificationDetails(notification?.data || {})?.map((item) => (
                  <Grid
                    key={item.label}
                    p={1}
                    mt={1}
                    borderRadius="md"
                    fontSize="sm"
                    fontWeight={500}
                    px={6}
                    bg={{ base: 'gray.50', _dark: 'gray.800' }}
                    templateColumns={{ base: '1fr', md: '220px minmax(0, 1fr)' }}
                    columnGap={4}
                    rowGap={1}
                    alignItems="flex-start"
                  >
                    <GridItem>
                      <FaceliftText fontSize="sm" fontWeight={600} wordBreak="break-word">
                        {item.label}
                      </FaceliftText>
                    </GridItem>
                    <GridItem>
                      <Show when={!item.url}>
                        <FaceliftText fontSize="sm" fontWeight={400} wordBreak="break-all" whiteSpace="pre-wrap">
                          {item.value}
                        </FaceliftText>
                      </Show>
                      <Show when={!!item.url}>
                        <Link
                          fontSize="sm"
                          fontWeight={400}
                          wordBreak="break-all"
                          whiteSpace="pre-wrap"
                          href={item.url || ''}
                          _hover={{ color: 'brand.500', cursor: 'pointer' }}
                          color="brand.600"
                        >
                          {item.value}
                        </Link>
                      </Show>
                    </GridItem>
                  </Grid>
                ))}
              </Box>
            </Box>
            <Show
              when={
                notification?.data?.entityName === 'ta_benefitsverification' ||
                notification?.data?.entityName === 'ta_orderrequest' ||
                notification?.data?.entityName === 'incident' ||
                notification?.data?.entityName === 'ta_returnrequest'
              }
            >
              <Box>
                <FaceliftButton
                  onClick={handleSeeDetails}
                >
                  See details
                </FaceliftButton>
              </Box>
            </Show>
          </Box>
        </Show>
      </Box>

      {/* Benefit Verification Details Dialog */}
      <FaceliftDialog
        open={showDetailsModal === 'bv'}
        onOpenChange={(openState) => {
          if (!openState) {
            setShowDetailsModal(null);
          }
        }}
        title="Details"
        showBackBtn={true}
        size="xl"
        sx={{
          boxShadow: `0 0 20px 0px ${brand600}`,
          width: '90vw',
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Show when={isLoadingBVDetails}>
          <FaceliftDataLoader
            title="Loading..."
            description="Please wait while we fetch the benefit verification details."
          />
        </Show>
        <Show when={!isLoadingBVDetails && !!bvDetails && !!accountProducts}>
          {transformBVRecordToPreview(bvDetails || null, accountProducts) && (
            <Preview
              bvInformation={transformBVRecordToPreview(bvDetails || null, accountProducts)!}
            />
          )}
        </Show>
      </FaceliftDialog>

      {/* Order Request Details Dialog */}
      <FaceliftDialog
        open={showDetailsModal === 'order'}
        onOpenChange={(openState) => {
          if (!openState) {
            setShowDetailsModal(null);
          }
        }}
        title="Details"
        showBackBtn={true}
        sx={{
          width: '1000px',
          maxWidth: '90vw',
          boxShadow: `0 0 20px 0px ${brand600}`,
        }}
      >
        <Show when={isOrderDetailsLoading}>
          <FaceliftDataLoader
            title="Loading..."
            description="Please wait while we fetch the latest order information."
          />
        </Show>
        <Show when={!isOrderDetailsLoading}>
          <DetailsView
            data={
              orderDetails?.data
                ? parseOrderDetailsForModal(orderDetails.data)
                : []
            }
            isLoading={false}
            gridConfig={{ base: '1fr', md: 'repeat(2, 1fr)' }}
          />
        </Show>
      </FaceliftDialog>

      {/* Support Ticket (Incident) Details Dialog */}
      <FaceliftDialog
        showBackBtn={true}
        title="Details"
        open={showDetailsModal === 'incident'}
        onOpenChange={(open) => {
          if (!open) setShowDetailsModal(null);
        }}
        sx={{
          width: '800px',
          maxWidth: '95vw',
          maxHeight: '95vh',
          boxShadow: `0 0 20px 0px ${brand600}`,
          overflowY: 'auto',
        }}
      >
        <Show when={isTicketDetailsLoading}>
          <FaceliftDataLoader
            title="Loading..."
            description="Please wait while we fetch the ticket information."
          />
        </Show>
        <Show when={!isTicketDetailsLoading}>
          <SupportTicketDetails data={ticketDetails || null} />
        </Show>
      </FaceliftDialog>

      {/* Return Order Details Dialog */}
      <FaceliftDialog
        open={showDetailsModal === 'return'}
        onOpenChange={(openState) => {
          if (!openState) {
            setShowDetailsModal(null);
          }
        }}
        title="Details"
        showBackBtn={true}
        size="xl"
        sx={{
          width: 980,
          maxWidth: '95vw',
          boxShadow: `0 0 20px 0px ${brand600}`,
          overflowY: 'auto',
        }}
      >
        <Show
          when={showDetailsModal === 'return' && !!notification?.data?.primaryEntityId}
        >
          <OrderReturnDetails returnOrderId={notification?.data?.primaryEntityId as string} />
        </Show>
      </FaceliftDialog>
    </Box>
  );
};

export default NotificationDetailsView;